import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  KeycloakGroup,
  KeycloakRole,
  UserKeycloak,
  UserWithRolesDTO,
} from '../../shared/interfaces/keycloak/userKeycloak';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GestionComptesService } from '../../shared/services/gestion-comptes.service';
import { filter, Subject, takeUntil, tap } from 'rxjs';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { SnackbarService } from 'src/app/shared/services/snackbar/snackbar.service';
import { SnackbarHelper } from 'src/app/shared/services/snackbar/snackbar.helper';
import { ConfirmationDialog } from 'src/app/shared/components/alert-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { CapitalizeDirective } from 'src/app/shared/directives/capitalize.directive';
import { UserSessionService } from 'src/app/shared/services/user.service';

@Component({
  selector: 'app-demandes',
  standalone: true,
  imports: [
    MatTableModule,
    MatIcon,
    MatCheckboxModule,
    MatIconButton,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatButton,
    CapitalizeDirective,
  ],
  templateUrl: './demandes.component.html',
  styleUrl: './demandes.component.scss',
})
export class DemandesComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  private idAdherent: number = 0;
  private nbAddedUsers: number = 0;

  private readonly currentUser: import('keycloak-js').KeycloakProfile;
  private availableRoles: KeycloakRole[] = [];
  readonly KEY_CURRENT_USER = 'currentUserAdmin';
  readonly KEY_ADMIN = 'adminCockpit';
  readonly ROLE_ACCUEIL = 'pfa.visualiser-accueil';
  readonly ROLE_ACCUEIL_GPO = 'pfa-gpo.visualiser-accueil';

  adminItems = [] as any[];

  displayedColumns: string[] = [
    'action',
    'lastName',
    'firstName',
    'email',
    'titulaire',
    'roles',
    'request',
  ];
  listeRolesGPO: Array<{ role: string; name: string }> = [
    { role: 'pfa-gpo.remise-grossiste', name: 'Simulation grossiste' },
    { role: 'pfa-gpo.remise-generique', name: 'Simulation générique' },
  ];
  listeRoles: Array<{ role: string; name: string }> = [];

  dataSourceDemandes = new MatTableDataSource<UserKeycloak>();
  activiteAdherent!: string;

  /* FlurlException : quand on a des caractères qui ne sont pas accepté par KeyCloak => plutôt que de bloquer la saisie à l'écran, se baser sur Keycloak et prévoir l'exception depuis le service et le message "Format non valide pour la champ X" (ou message + générique : creation en échec contactez votre admin")
    TODO : Utilisateurs : rendre collaspable la liste des rôles d'un utilisateur
    TODO : Idem dans la liste des demandes ?
    TODO : Ajouter un message d'erreur si on clique sur Enregistrer alors qu'il n'y a aucune nouvelle ligne
  */

  constructor(
    private readonly gestionComptesService: GestionComptesService,
    private readonly snackbarService: SnackbarService,
    private readonly dialog: MatDialog,
    private readonly userSessionService: UserSessionService,
  ) {
    this.currentUser = JSON.parse(localStorage[this.KEY_CURRENT_USER] ?? '[]');
  }

  ngOnInit(): void {
    this.gestionComptesService.idAdherent
      .pipe(
        takeUntil(this._destroying$),
        tap((idAdherent) => {
          this.idAdherent = idAdherent;
          this.onAdherentChange(idAdherent);
        }),
      )
      .subscribe();
    this.adminItems = JSON.parse(localStorage[this.KEY_ADMIN] ?? '[]');
    this.activiteAdherent = this.userSessionService.getActivite();
    if (this.activiteAdherent == 'gpo') {
      this.listeRoles = this.listeRolesGPO;
    } else {
      this.listeRoles = this.adminItems;
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  getStatut(enabled: boolean): string {
    return enabled ? 'Actif' : 'Inactif';
  }

  onAdherentChange(idAdherent: number) {
    if (idAdherent == 0 || !idAdherent) {
      return;
    }
    this.getRequestsWithRoles(idAdherent);
  }

  getRequestsWithRoles(idAdherent: number) {
    // Call to the service
    this.gestionComptesService
      .getUsersByIdAdherent(idAdherent)
      .pipe(
        takeUntil(this._destroying$),
        tap((users: UserWithRolesDTO[]) => {
          let userKeycloaks: UserKeycloak[] = [];
          this.availableRoles =
            users.find((u) => u.email == this.currentUser.email)?.roles! ?? [];

          users.forEach((user: UserWithRolesDTO) => {
            if (!user.enabled) {
              userKeycloaks.push({
                id: user.id,
                idAzure: '',
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                roles: user.roles
                  .filter((role) => role.name.startsWith('pfa'))
                  .map((role) => role.name),
                groups: user.groups.map((group) => group.name),
                enabled: user.enabled,
                isInactivated: user.isInactivated,
              });
            }
          });

          this.dataSourceDemandes = new MatTableDataSource<UserKeycloak>(
            userKeycloaks,
          );
        }),
      )
      .subscribe();
  }

  onAdd() {
    let userKeycloaks: UserKeycloak[] = [];
    this.dataSourceDemandes.data.forEach((user: UserKeycloak) => {
      if (!user.enabled) {
        userKeycloaks.push({
          id: user.id,
          idAzure: '',
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          roles: user.roles,
          groups: user.groups,
          enabled: user.enabled,
          isInactivated: user.isInactivated,
        });
      }
    });
    this.nbAddedUsers++;
    let newUser: UserKeycloak = {
      id: '%' + this.nbAddedUsers.toString(),
      idAzure: '',
      firstName: '',
      lastName: '',
      email: '',
      roles: [
        this.activiteAdherent === 'gpo'
          ? this.ROLE_ACCUEIL_GPO
          : this.ROLE_ACCUEIL,
      ],
      groups: [],
      enabled: false,
      isInactivated: false,
    };
    userKeycloaks.push(newUser);

    this.dataSourceDemandes = new MatTableDataSource<UserKeycloak>(
      userKeycloaks,
    );
  }

  onSave() {
    let users: UserWithRolesDTO[] = this.convertUserKeycloakToUserDTO(
      this.dataSourceDemandes.data.filter((user) => user.id.startsWith('%')),
    );

    if (this.checkUsersData(users)) {
      return;
    }

    let validationMessage: string = `Valider les demandes ?`;
    if (users.some((u) => u.roles.length == 1)) {
      validationMessage =
        "Création d'un compte sans aucun module.\nVoulez-vous vraiment envoyer cette demande ?";
    }

    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        message: validationMessage,
        buttonText: {
          ok: 'Oui',
          cancel: 'Non',
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        let users: UserWithRolesDTO[] = this.convertUserKeycloakToUserDTO(
          this.dataSourceDemandes.data.filter((user) =>
            user.id.startsWith('%'),
          ),
        );

        this.gestionComptesService
          .createUsers(this.idAdherent, users, this.activiteAdherent)
          .pipe(
            takeUntil(this._destroying$),
            tap((result: UserWithRolesDTO[]) => {
              users = result;
              if (result.some((u) => u.existingEmail)) {
                this.snackbarService.show(
                  'Au moins un email demandé existe déjà, nous avons prévenu votre admin.',
                  SnackbarHelper.snackbarPanelClassSuccess(),
                );
              } else {
                this.snackbarService.show(
                  "La demande de création d'utilisateur(s) a été envoyée avec succès",
                  SnackbarHelper.snackbarPanelClassSuccess(),
                );
              }
            }),
          )
          .subscribe({
            error: (err) => {
              this.snackbarService.show(
                "Une erreur est survenue lors de la demande de création d'utilisateur : " +
                  err.message,
                SnackbarHelper.snackbarPanelClassError(),
              );
            },
            complete: () => {
              this.sendEmail(users);
              this.dataSourceDemandes.data.forEach(
                (row) => (row.id = row.id.replace('%', '')),
              );
            },
          });
      }
    });
  }

  public get dialogInstance() {
    return this.dialog;
  }

  sendEmail(users: UserWithRolesDTO[]) {
    this.gestionComptesService
      .sendMailCreateUsers(users, this.idAdherent, this.currentUser.email!)
      .pipe(
        takeUntil(this._destroying$),
        tap((result: UserWithRolesDTO[]) => {}),
      )
      .subscribe();
    // On n'avertit pas l'adhérent si l'email n'est pas bien parti
  }

  private convertUserKeycloakToUserDTO(
    userKeycloak: UserKeycloak[],
  ): UserWithRolesDTO[] {
    let result: UserWithRolesDTO[] = [];

    userKeycloak.forEach((user) => {
      let userDTO: UserWithRolesDTO = new User(
        user.id,
        user.email,
        user.firstName,
        user.lastName,
        user.roles,
        user.groups,
        this.availableRoles,
        user.isInactivated,
        false,
      );
      result.push(userDTO);
    });

    return result;
  }

  /**
   * Delete a row from the list of requests
   * @param {UserKeycloak} deletedRow - The row to be deleted.
   * */
  onDeleteDemande(deletedRow: UserKeycloak) {
    this.dataSourceDemandes.data = this.dataSourceDemandes.data.filter(
      (row) => row.id != deletedRow.id,
    );
  }

  setSelected(isChecked: boolean, role: string, user: UserKeycloak | null) {
    if (!user) {
      return;
    }
    if (isChecked) {
      if (!user.roles.includes(role)) {
        user.roles.push(role);
      }
    } else {
      user.roles = user.roles.filter((r) => r !== role);
    }
  }

  getTitulaireRole(user: any): string {
    return this.activiteAdherent === 'gpo'
      ? 'pfa-gpo.titulaire'
      : 'pfa.titulaire';
  }

  isEditable(user: UserKeycloak) {
    return user.id.startsWith('%');
  }

  private isEmailValid(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailPattern.test(email);
  }

  private isNameValid(name: string): boolean {
    /*
    - Une majuscule au début de chaque mot
    - Un ou plusieurs mots séparés par des espaces
    - Des mots constitués de lettres minuscules, majuscules et / ou de caractères parmi ceux de la liste : éèàùôïüçÉÈÀÙÔÏÜÇ'-

    */
    const nameOrFirstnamePattern =
      /^[A-Z][a-zA-ZéèàùôïüçÉÈÀÙÔÏÜÇ'-]+( [a-zA-ZéèàùôïüçÉÈÀÙÔÏÜÇ'-]+)*$/;
    return nameOrFirstnamePattern.test(name);
  }

  private checkUsersData(users: UserWithRolesDTO[]): boolean {
    if (users == null || users.length < 1) {
      this.snackbarService.show(
        "Veuillez ajouter au moins une demande avant d'enregistrer",
        SnackbarHelper.snackbarPanelClassError(),
      );
      return true;
    }
    let incompleteUser: UserWithRolesDTO | undefined =
      users.find(
        (u) =>
          u.email.trim().length == 0 ||
          u.lastName.trim().length == 0 ||
          u.firstName.trim().length == 0,
      ) ?? undefined;
    if (incompleteUser != null && incompleteUser != undefined) {
      this.snackbarService.show(
        "Il manque au moins l'email, le nom ou le prénom sur une des lignes",
        SnackbarHelper.snackbarPanelClassError(),
      );
      return true;
    }

    // Email

    let userBadEmail: UserWithRolesDTO | undefined =
      users.find((u) => !this.isEmailValid(u.email)) ?? undefined;
    if (userBadEmail != null) {
      this.snackbarService.show(
        "Le format de l'email est incorrect : " + userBadEmail.email,
        SnackbarHelper.snackbarPanelClassError(),
      );
      return true;
    }

    let tooLongEmail: UserWithRolesDTO | undefined =
      users.find((u) => u.email.length > 320) ?? undefined;
    if (tooLongEmail != null) {
      this.snackbarService.show(
        "L'email est trop long : " + tooLongEmail.email,
        SnackbarHelper.snackbarPanelClassError(),
      );
      return true;
    }

    let userBadLastname: UserWithRolesDTO | undefined =
      users.find((u) => !this.isNameValid(u.lastName)) ?? undefined;
    if (userBadLastname != undefined) {
      this.snackbarService.show(
        'Le format du nom de famille est incorrect ou trop court : ' +
          userBadLastname.lastName,
        SnackbarHelper.snackbarPanelClassError(),
      );
      return true;
    }

    let tooLongLastname: UserWithRolesDTO | undefined =
      users.find((u) => u.lastName.length > 250) ?? undefined;
    if (tooLongLastname != undefined) {
      this.snackbarService.show(
        'Le nom de famille est trop long : ' + tooLongLastname.lastName,
        SnackbarHelper.snackbarPanelClassError(),
      );
      return true;
    }

    let userBadFirstname: UserWithRolesDTO | undefined =
      users.find((u) => !this.isNameValid(u.firstName)) ?? undefined;
    if (userBadFirstname != null) {
      this.snackbarService.show(
        'Le format du prénom est incorrect ou trop court : ' +
          userBadFirstname.firstName,
        SnackbarHelper.snackbarPanelClassError(),
      );
      return true;
    }

    let tooLongFirstname: UserWithRolesDTO | undefined =
      users.find((u) => u.firstName.length > 250) ?? undefined;
    if (tooLongFirstname != null) {
      this.snackbarService.show(
        'Le prénom est trop long : ' + tooLongFirstname.firstName,
        SnackbarHelper.snackbarPanelClassError(),
      );
      return true;
    }

    return false;
  }
}

class User implements UserWithRolesDTO {
  createdTimestamp: number;
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  enabled: boolean;
  roles: KeycloakRole[];
  groups: KeycloakGroup[];
  poste: string;
  tempPassword: string;
  isInactivated: boolean;
  existingEmail: boolean;
  readonly KEY_ADMIN = 'adminCockpit';

  // Constructeur
  constructor(
    id: string,
    email: string,
    firstName: string,
    lastName: string,
    roles: string[],
    groups: string[],
    availableRoles: any[],
    isInactivated: boolean,
    existingEmail: boolean,
  ) {
    this.id = id;
    this.createdTimestamp = Date.now();
    this.email = email;
    this.enabled = false;
    this.firstName = firstName;
    this.poste = '';
    this.tempPassword = '';
    this.lastName = lastName;
    this.username = email;
    this.roles = this.getRoles(roles, availableRoles);
    this.groups = [];
    this.isInactivated = isInactivated;
    this.existingEmail = existingEmail;
  }

  getRoles(roles: string[], availableRoles: KeycloakRole[]): KeycloakRole[] {
    type RoleWithName = { role: string; name: string };

    const tableauRoles: RoleWithName[] = JSON.parse(
      localStorage[this.KEY_ADMIN] ?? '[]',
    );

    return roles.map((role) => {
      const storedRole = tableauRoles.find((r) => r.role === role);

      const roleName = storedRole?.role ?? role;

      const foundRole = availableRoles.find((a) => a.name === roleName);

      if (foundRole) {
        return foundRole;
      }

      return {
        id: '',
        name: roleName,
        composite: false,
        clientRole: false,
      } as KeycloakRole;
    });
  }
}
